import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenantSafe } from '../context/TenantContext';
import {
  getStats,
  getSummary,
  getExports,
  getExport,
  createExport,
  getMyConsent,
  updateMyConsent,
} from '../services/communityDataService';
import { getProjects } from '../services/projectService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const RESPONSE_SCOPES = [
  { value: 'community_only',           label: 'Community only' },
  { value: 'community_plus_platform',  label: 'Community + platform' },
  { value: 'public',                   label: 'Public' },
];

const MEMBERSHIP_STATUSES = [
  { value: 'community_verified', label: 'Verified' },
  { value: 'community_pending',  label: 'Pending' },
  { value: 'anonymous',          label: 'Anonymous' },
];

const CONSENT_LABELS = {
  allowAnalytics:            'Allow use in community analytics',
  allowCrossTenantAnalytics: 'Allow anonymised cross-community analytics',
  allowModelTraining:        'Allow use in AI/model training',
  allowExport:               'Allow inclusion in dataset exports',
};

const toggleSet = (set, value) => {
  const next = new Set(set);
  next.has(value) ? next.delete(value) : next.add(value);
  return next;
};

// ─── DistributionBar ─────────────────────────────────────────────────────────

const DistributionBar = ({ distribution }) => {
  const entries = Object.entries(distribution);
  const total   = entries.reduce((s, [, n]) => s + n, 0);
  if (total === 0) return <em>No answers</em>;
  return (
    <ul className="distribution-list">
      {entries.sort(([, a], [, b]) => b - a).map(([val, count]) => (
        <li key={val} className="distribution-item">
          <span className="distribution-label">{val || <em>blank</em>}</span>
          <span className="distribution-bar-wrap">
            <span
              className="distribution-bar-fill"
              style={{ width: `${Math.round((count / total) * 100)}%` }}
            />
          </span>
          <span className="distribution-count">{count} ({Math.round((count / total) * 100)}%)</span>
        </li>
      ))}
    </ul>
  );
};

// ─── StatsView ────────────────────────────────────────────────────────────────

const StatsView = ({ tenantId }) => {
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});  // questionnaireId → bool

  useEffect(() => {
    getStats({ tenantId })
      .then((r) => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [tenantId]);

  if (loading) return <p className="page-loading">Loading stats…</p>;
  if (!stats)  return <p className="error-msg">Could not load stats.</p>;

  const toggle = (qId) => setExpanded((prev) => ({ ...prev, [qId]: !prev[qId] }));

  return (
    <div className="stats-view">
      <div className="stats-summary-grid">
        <div className="stats-card">
          <span className="stats-card__value">{stats.totalResponses}</span>
          <span className="stats-card__label">Total Responses</span>
        </div>
        {Object.entries(stats.byMembershipStatus).map(([status, count]) => (
          <div className="stats-card" key={status}>
            <span className="stats-card__value">{count}</span>
            <span className="stats-card__label">{status.replace(/_/g, ' ')}</span>
          </div>
        ))}
      </div>

      {stats.byQuestionnaire.length === 0 ? (
        <p>No response data yet.</p>
      ) : (
        stats.byQuestionnaire.map((q) => (
          <div key={q.questionnaireId} className="questionnaire-stats-block">
            <button
              type="button"
              className="collapsible-header"
              onClick={() => toggle(q.questionnaireId)}
            >
              <span>{q.title}</span>
              <span className="collapsible-header__count">{q.count} response{q.count !== 1 ? 's' : ''}</span>
              <span>{expanded[q.questionnaireId] ? '▲' : '▼'}</span>
            </button>

            {expanded[q.questionnaireId] && (
              <div className="collapsible-body">
                {q.fieldDistributions.length === 0 ? (
                  <p>No answer data.</p>
                ) : (
                  q.fieldDistributions.map((fd) => (
                    <div key={fd.fieldKey} className="field-distribution">
                      <h5>{fd.label} <small>({fd.type})</small></h5>
                      <DistributionBar distribution={fd.distribution} />
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

// ─── ExportCreator ────────────────────────────────────────────────────────────

const ExportCreator = ({ tenantId, projects, onCreated, onCancel }) => {
  const [label, setLabel]         = useState('');
  const [projectId, setProjectId] = useState('');
  const [scopes, setScopes]       = useState(new Set(['community_only']));
  const [statuses, setStatuses]   = useState(new Set(['community_verified']));
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!label.trim()) { setMsg('Label is required.'); return; }
    setSaving(true);
    setMsg('');
    try {
      const res = await createExport({
        tenantId,
        projectId: projectId || undefined,
        label,
        filters: {
          responseScope:    [...scopes],
          membershipStatus: [...statuses],
        },
      });
      onCreated(res.data);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Export failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="export-creator" onSubmit={handleSubmit}>
      <h4>New Dataset Export</h4>

      <label>
        Label
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Q1 2026 — Verified Members"
          required
        />
      </label>

      <label>
        Project filter <small>(optional)</small>
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          <option value="">All projects</option>
          {projects.map((p) => (
            <option key={p._id} value={p._id}>{p.title}</option>
          ))}
        </select>
      </label>

      <fieldset>
        <legend>Response scopes to include</legend>
        {RESPONSE_SCOPES.map(({ value, label: scLabel }) => (
          <label key={value} className="checkbox-label">
            <input
              type="checkbox"
              checked={scopes.has(value)}
              onChange={() => setScopes(toggleSet(scopes, value))}
            />
            {scLabel}
          </label>
        ))}
      </fieldset>

      <fieldset>
        <legend>Membership statuses to include</legend>
        {MEMBERSHIP_STATUSES.map(({ value, label: msLabel }) => (
          <label key={value} className="checkbox-label">
            <input
              type="checkbox"
              checked={statuses.has(value)}
              onChange={() => setStatuses(toggleSet(statuses, value))}
            />
            {msLabel}
          </label>
        ))}
      </fieldset>

      <p className="info-msg">
        Only responses from users who have enabled <em>Allow inclusion in dataset exports</em> in their
        consent settings will be included. Anonymous responses are always eligible.
      </p>

      {msg && <p className="error-msg">{msg}</p>}

      <div className="form-actions">
        <button type="submit" disabled={saving || scopes.size === 0}>
          {saving ? 'Generating…' : 'Generate Export'}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

// ─── ExportPanel ─────────────────────────────────────────────────────────────

const ExportPanel = ({ tenantId, projects }) => {
  const [exports, setExports]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);   // full export with records
  const [loadingExp, setLoadingExp] = useState(false);

  const load = useCallback(() => {
    getExports({ tenantId })
      .then((r) => setExports(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleCreated = (exp) => {
    setExports((prev) => [exp, ...prev]);
    setCreating(false);
  };

  const handleViewExport = async (exp) => {
    if (selected?._id === exp._id) { setSelected(null); return; }
    setLoadingExp(true);
    try {
      const res = await getExport(exp._id);
      setSelected(res.data);
    } catch {
      setSelected(null);
    } finally {
      setLoadingExp(false);
    }
  };

  if (creating) {
    return (
      <ExportCreator
        tenantId={tenantId}
        projects={projects}
        onCreated={handleCreated}
        onCancel={() => setCreating(false)}
      />
    );
  }

  if (loading) return <p className="page-loading">Loading exports…</p>;

  return (
    <div className="export-panel">
      <div className="section-header">
        <h4>Dataset Exports</h4>
        <button onClick={() => setCreating(true)}>+ New Export</button>
      </div>

      {exports.length === 0 ? (
        <p>No exports yet. Generate one to create a consent-filtered, de-identified snapshot.</p>
      ) : (
        <>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Label</th><th>v</th><th>Responses</th><th>Consent coverage</th><th>Created</th><th></th>
              </tr>
            </thead>
            <tbody>
              {exports.map((exp) => (
                <tr key={exp._id} className={selected?._id === exp._id ? 'row-selected' : ''}>
                  <td>{exp.label}</td>
                  <td>v{exp.version}</td>
                  <td>{exp.recordCount}</td>
                  <td>
                    {exp.consentSnapshot?.profilesChecked > 0
                      ? `${exp.consentSnapshot.profilesAllowed}/${exp.consentSnapshot.profilesChecked} profiles`
                      : '—'}
                  </td>
                  <td>{new Date(exp.createdAt).toLocaleDateString()}</td>
                  <td>
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => handleViewExport(exp)}
                    >
                      {selected?._id === exp._id ? 'Hide' : 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {loadingExp && <p>Loading export…</p>}

          {selected && !loadingExp && (
            <div className="export-records">
              <h5>{selected.label} — v{selected.version}</h5>
              <p className="info-msg">
                {selected.recordCount} responses • consent: {selected.consentSnapshot?.profilesAllowed}/{selected.consentSnapshot?.profilesChecked} profiles •
                generated {new Date(selected.consentSnapshot?.exportedAt || selected.createdAt).toLocaleString()}
              </p>
              {(selected.records || []).map((q) => (
                <div key={q.questionnaireId} className="questionnaire-stats-block">
                  <div className="collapsible-header">
                    <span>{q.title}</span>
                    <span className="collapsible-header__count">{q.count} response{q.count !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="collapsible-body">
                    {q.fieldDistributions.map((fd) => (
                      <div key={fd.fieldKey} className="field-distribution">
                        <h5>{fd.label}</h5>
                        <DistributionBar distribution={fd.distribution} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── ManagerView ──────────────────────────────────────────────────────────────

const ManagerView = ({ tenantId, activeTenant }) => {
  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState('stats');

  useEffect(() => {
    getProjects(tenantId).then((r) => setProjects(r.data)).catch(() => {});
  }, [tenantId]);

  const communityName = activeTenant?.branding?.displayName || activeTenant?.name;

  return (
    <div className="community-data-manager">
      <h3>Community Data — {communityName}</h3>

      <div className="tabs">
        <button className={activeTab === 'stats'   ? 'active' : ''} onClick={() => setActiveTab('stats')}>
          Response Stats
        </button>
        <button className={activeTab === 'exports' ? 'active' : ''} onClick={() => setActiveTab('exports')}>
          Dataset Exports
        </button>
      </div>

      {activeTab === 'stats'   && <StatsView  tenantId={tenantId} />}
      {activeTab === 'exports' && <ExportPanel tenantId={tenantId} projects={projects} />}
    </div>
  );
};

// ─── ConsentPanel ─────────────────────────────────────────────────────────────

const ConsentPanel = ({ tenantId }) => {
  const [permissions, setPermissions] = useState({
    allowAnalytics:            false,
    allowCrossTenantAnalytics: false,
    allowModelTraining:        false,
    allowExport:               false,
  });
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState('');
  const [msgType, setMsgType]   = useState('');

  useEffect(() => {
    getMyConsent({ tenantId })
      .then((r) => setPermissions(r.data.permissions))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [tenantId]);

  const handleToggle = (key) =>
    setPermissions((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    try {
      await updateMyConsent({ tenantId, permissions });
      setMsg('Preferences saved.');
      setMsgType('success');
    } catch (err) {
      setMsg(err.response?.data?.message || 'Save failed');
      setMsgType('error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="page-loading">Loading consent settings…</p>;

  return (
    <form className="consent-panel" onSubmit={handleSave}>
      <h4>Your Data Preferences</h4>
      <p>
        Control how your responses may be used. You can change these at any time.
        Changes take effect on the next data export.
      </p>

      {Object.entries(CONSENT_LABELS).map(([key, label]) => (
        <label key={key} className="checkbox-label consent-item">
          <input
            type="checkbox"
            checked={permissions[key] || false}
            onChange={() => handleToggle(key)}
          />
          <span>{label}</span>
        </label>
      ))}

      {msg && <p className={msgType === 'success' ? 'success-msg' : 'error-msg'}>{msg}</p>}

      <div className="form-actions">
        <button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Preferences'}</button>
      </div>
    </form>
  );
};

// ─── MemberView ───────────────────────────────────────────────────────────────
// Active members see a community-level response summary + their consent settings.
const MemberView = ({ tenantId, activeTenant }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSummary({ tenantId })
      .then((r) => setSummary(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [tenantId]);

  const communityName = activeTenant?.branding?.displayName || activeTenant?.name;

  return (
    <div className="community-data-member">
      <h3>Community Data — {communityName}</h3>

      <section className="data-summary-section">
        <h4>Community Response Summary</h4>
        {loading ? (
          <p className="page-loading">Loading…</p>
        ) : !summary || summary.totalResponses === 0 ? (
          <p>No community responses collected yet.</p>
        ) : (
          <>
            <p><strong>{summary.totalResponses}</strong> community-scoped response{summary.totalResponses !== 1 ? 's' : ''} collected.</p>
            <ul className="questionnaire-list">
              {summary.byQuestionnaire.map((q) => (
                <li key={q.questionnaireId} className="questionnaire-card">
                  <div className="questionnaire-card__body">
                    <h4>{q.title}</h4>
                  </div>
                  <span className="questionnaire-card__meta">{q.count} response{q.count !== 1 ? 's' : ''}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section className="consent-section">
        <ConsentPanel tenantId={tenantId} />
      </section>
    </div>
  );
};

// community_admin / community_staff → ManagerView (full stats + export management)
// member → MemberView (summary counts + consent settings)
const CommunityDataPage = ({ mode }) => {
  const { access, activeTenant, panel } = useAuth();
  const tenantCtx = useTenantSafe();
  const tenantId = tenantCtx?.viewTenant?._id || activeTenant?._id;
  const communityRole = tenantCtx?.viewerRole || (tenantId ? access?.communityRoles?.[tenantId] : null);

  const effectiveMode = mode || (() => {
    if (tenantCtx?.isPlatformStaff || panel === 'admin' || panel === 'staff') return 'oversight';
    if (communityRole === 'community_admin' || communityRole === 'community_staff') return 'review';
    return 'contribute';
  })();

  // oversight: platform admin/staff see ManagerView scoped to all data (no tenantId guard)
  // For now, staff without an activeTenant still see the manager view with no filter.
  if (effectiveMode === 'oversight' || effectiveMode === 'review') {
    return (
      <div className="page community-data-page">
        <ManagerView tenantId={tenantId} activeTenant={activeTenant} />
      </div>
    );
  }

  if (!tenantId) {
    return (
      <div className="page community-data-page">
        <p className="page-loading">No active community.</p>
      </div>
    );
  }

  return (
    <div className="page community-data-page">
      <MemberView tenantId={tenantId} activeTenant={activeTenant} />
    </div>
  );
};

export default CommunityDataPage;
